import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { App } from "antd";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseTextArea from "@/components_v2/FormFields/Input/BaseTextArea";
import BaseStatusChip from "@/components_v2/StatusChip/BaseStatusChip";
import { STATUS_CHIP_VARIANTS } from "@/components_v2/StatusChip/constants";
import BaseTag from "@/components_v2/Tags/BaseTag";
import { useReviewContributionMutation } from "@/redux/service";
import type { ReviewContributionRequest } from "@/redux/certifications/types";
import {
  certificationPaths,
  certificationRouteKey,
} from "@/domains/certifications/routes";
import { getInitials, getWeekNumber } from "@/domains/certifications/utils";
import ContributionBody from "@/features/certifications/community-flags/components/ContributionBody";
import { FROM_DISCUSSION_FLAGS } from "@/features/certifications/community-flags/constants";
import { useContributionContext } from "@/features/certifications/community-flags/hooks/useContributionContext";
import { getQueueVerdict } from "@/features/certifications/community-flags/utils";
import { GradeCardProps } from "@/features/certifications/community-flags/types";
import { CONTRIBUTION_VERDICTS } from "@/domains/certifications/constants";

import "../styles.css";

const GradeCard = ({
  certification,
  grade,
  feedback,
  onFeedbackChange,
}: GradeCardProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const context = useContributionContext(grade);
  const [review, { isLoading, originalArgs }] = useReviewContributionMutation();
  const deciding = isLoading ? originalArgs?.verdict : undefined;

  const name = context.author?.fullName.trim() || grade.authorEmail;
  const firstName = context.author?.firstName.trim() || name;
  const feedbackPlaceholder = t(
    "certification.admin.communityFlags.feedbackPlaceholder",
    { name: firstName },
  );
  const week = getWeekNumber(certification.modules, grade.module);
  const kind = grade.contentKind;

  const handleReview = async (
    verdict: ReviewContributionRequest["verdict"],
  ) => {
    try {
      await review({ gradeId: grade.id, verdict, feedback }).unwrap();
      notification.success({
        message: t(
          `certification.admin.communityFlags.decided.${verdict}.${kind}`,
          { name },
        ),
      });
    } catch (error) {
      notification.error({
        message: t(
          (error as FetchBaseQueryError).status === 409
            ? "certification.admin.communityFlags.alreadyDecided"
            : "certification.admin.communityFlags.decideError",
        ),
      });
    }
  };

  return (
    <article className="flag-card flex flex-col">
      <header className="flag-card-header flex flex-wrap items-center justify-between gap-200">
        <div className="flex items-center gap-200">
          <BaseAvatar
            size="default"
            className="flag-card-avatar"
            label={getInitials(name)}
            imageUrl={context.author?.profilePicture}
            aria-hidden
          />

          <Link
            to={certificationPaths.participant(
              certificationRouteKey(certification),
              grade.author,
            )}
            state={FROM_DISCUSSION_FLAGS}
            className="flag-card-author body-s-medium"
          >
            {name}
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-200">
          <BaseStatusChip
            label={t("certification.admin.communityFlags.visible")}
            variant={STATUS_CHIP_VARIANTS.BLUE_TINT}
          />

          {week !== null && (
            <BaseTag
              size="small"
              label={t("certification.labels.week", { position: week })}
            />
          )}

          <time
            dateTime={grade.submittedAt}
            className="flag-card-muted body-s-regular"
          >
            {DateTime.fromISO(grade.submittedAt).toRelative()}
          </time>
        </div>
      </header>

      <ContributionBody
        certification={certification}
        grade={grade}
        context={context}
        showActivity
      />

      <footer className="flag-card-footer flex flex-col gap-300">
        <div className="flex flex-col gap-100">
          <div className="flex flex-wrap items-center gap-200">
            <span className="flag-card-eyebrow body-xs-regular">
              {t("certification.admin.communityFlags.aiCheck")}
            </span>

            <BaseStatusChip
              label={t(
                `certification.admin.communityFlags.verdicts.${getQueueVerdict(grade)}`,
              )}
              variant={STATUS_CHIP_VARIANTS.AMBER}
            />
          </div>

          {grade.aiRationale && (
            <p className="flag-card-note body-s-regular">{grade.aiRationale}</p>
          )}
        </div>

        <div className="flex flex-wrap items-end justify-between gap-300">
          <div className="flag-feedback flex flex-col gap-100">
            <BaseTextArea
              value={feedback}
              autoSize={{ minRows: 2, maxRows: 5 }}
              aria-label={feedbackPlaceholder}
              placeholder={feedbackPlaceholder}
              onChange={(event) =>
                onFeedbackChange(grade.id, event.target.value)
              }
            />
          </div>

          <div className="flex flex-wrap gap-200">
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              loading={deciding === CONTRIBUTION_VERDICTS.OFF_TOPIC}
              disabled={isLoading}
              onClick={() => handleReview(CONTRIBUTION_VERDICTS.OFF_TOPIC)}
            >
              {t("certification.admin.communityFlags.doesNotCount")}
            </BaseButton>

            <BaseButton
              size={BUTTON_SIZES.SMALL}
              loading={deciding === CONTRIBUTION_VERDICTS.ON_TOPIC}
              disabled={isLoading}
              onClick={() => handleReview(CONTRIBUTION_VERDICTS.ON_TOPIC)}
            >
              {t("certification.admin.communityFlags.counts")}
            </BaseButton>
          </div>
        </div>
      </footer>
    </article>
  );
};

export default GradeCard;
